import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { observer } from 'mobx-react';
import React, { FC, useState } from 'react';
import styles from './domain-overlap.module.scss';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { Dropdown, option } from '@monorepo/base/src/components/dropdown/dropdown';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../modules/stores';
import { useStateBetweenPages } from '../../../modules/hooks/tools/use-state-between-pages';
import { useList } from '../../../modules/hooks/tools/use-list';
import { BidderCampaignTableModel } from '../../../modules/models/bidder-campaign/bidder-camapign.model';
import { sortData } from '../../../modules/hooks/tools/use-merge-with-performance';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { SpacerY } from '@monorepo/base/src/components/spacer/spacer';
import { BidderDomainsApi } from '../../../modules/apis/bidder-domains.api';
import { ICampaignConflictData } from '../../../modules/models/bidder-domains.model';
import { useDomainOverlapHeaders } from '../../../modules/hooks/theaders/domain-overlap.headers';
import { useRoute } from '../../../modules/hooks/tools/use-route';

const pageBarDebugProps = { dataAttrs: [new DataAttribute('id', 'domain-overlap')] };

export const DomainOverlap: FC = observer(() => {
	const { bidderStore } = useStores<IAdminxStore>();
	const [error, setError] = useState<string>('');
	const [isLoadingAnalyze, setIsLoadingAnalyze] = useState<boolean>(false);
	const [selectedCampaign, setSelectedCampaign] = useState<BidderCampaignTableModel | undefined>(undefined);
	const [resultData, setResultData] = useState<ICampaignConflictData | null>(null);

	const sortBy = usePageSettings().tableStore?.getSortingBy();
	const bidderStateBetweenPages = useStateBetweenPages(() => bidderStore.campaignsList.getList().length !== 0);
	useList<BidderCampaignTableModel>(bidderStore.campaignsList, undefined, bidderStateBetweenPages);

	const { currentRouteWithoutSpecialChars } = useRoute();
	const { columns } = useDomainOverlapHeaders(selectedCampaign?.id);
	const sortedCampaigns = sortData(bidderStore.campaignsList.getList().slice(), sortBy?.[0]);
	const campaignOptions = sortedCampaigns.map(campaign => `${campaign.id} - ${campaign.name}`);

	const conflictData = resultData?.conflict_data || [];
	const sortedConflictData = sortData(conflictData, sortBy?.[0]);
	const onCampaignSelect = (options: Set<option> | undefined) => {
		if (!options) {
			return;
		}
		const optionsArr = Array.from(options);
		const selectedOption = optionsArr[0];
		if (selectedOption) {
			const campaignId = parseInt(selectedOption.split(' - ')[0]);
			const campaign = sortedCampaigns.find(c => c.id === campaignId);
			if (campaign !== selectedCampaign) {
				setSelectedCampaign(campaign);
				setError('');
				setResultData(null);
			} else {
				return;
			}
		}
	};

	const analyzeDomainsStates = async () => {
		if (!selectedCampaign) {
			setError('Please Select Campaign');
			return;
		}

		setIsLoadingAnalyze(true);
		const selectedCampaignId = selectedCampaign.id;

		try {
			const response = await BidderDomainsApi.getCampaignConflictData(selectedCampaignId);
			if (response.campaign_conflict_data) {
				setResultData(response.campaign_conflict_data);
			} else {
				setResultData(null);
			}
			setError('');
		} catch (err) {
			setError('Failed to analyze domains');
			console.error('Error analyzing domains:', err);
		} finally {
			setIsLoadingAnalyze(false);
		}
	};

	return (
		<Page>
			<Pagebar debugProps={pageBarDebugProps}>
				<PagebarTitle>Domain Overlap</PagebarTitle>
			</Pagebar>

			<div className={styles.wrapper}>
				<Dropdown
					label={'Select Campaign'}
					placeholder={'Search By CampaignID...'}
					options={campaignOptions}
					onSelect={onCampaignSelect}
					disabled={bidderStore.campaignsList.getIsLoading()}
					autocomplete={true}
					autocompletePlaceholder={'Search By CampaignID...'}
					isLoading={bidderStore.campaignsList.getIsLoading()}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_search_dropdown')] }}
				/>
				<SpacerY />
				{resultData && conflictData.length === 0 && !isLoadingAnalyze && (
					<div className={styles.errorMsg}>No conflicts found for the selected campaign.</div>
				)}
				<PrimaryButton
					disabled={!selectedCampaign || isLoadingAnalyze}
					onClick={analyzeDomainsStates}
					className={styles.analayzeBtn}
					icon={'search-sm'}
					isRotateIcon={false}>
					Analyze
				</PrimaryButton>
			</div>
			<SpacerY />

			<div className={styles.tableWrapper}>
				{conflictData.length > 0 && (
					<ControlledTable
						classes={{ wrapper: styles.table }}
						currentRoute={currentRouteWithoutSpecialChars}
						columns={columns}
						data={sortedConflictData}
						defaultSortBy={sortBy}
						isLoading={isLoadingAnalyze}
					/>
				)}
			</div>
			{error && <div className={styles.errorMsg}>{error}</div>}
		</Page>
	);
});
